/**
 * Basic Geospatial Calculations
 *
 * This module provides core geometric utilities for agricultural plot visualization.
 * It handles AB line bearing calculations and coordinate rotation for proper plot alignment.
 *
 * Key responsibilities:
 * - Calculate AB line bearings for plot orientation
 * - Rotate coordinates for proper plot alignment with AB lines
 * - Generate rotated plot corner coordinates
 *
 * Used by: plotVisualization.ts, visualizationBuilder.ts
 */

import * as turf from '@turf/turf';

// Helper function to calculate bearing from AB line
export function getABLineBearing(ablineGeoJSON: any): number | null {
  if (!ablineGeoJSON?.features?.[0]?.geometry?.coordinates) {
    return null;
  }

  const coords = ablineGeoJSON.features[0].geometry.coordinates;
  if (coords.length < 2) {
    return null;
  }

  // Calculate bearing using turf
  const startPoint = turf.point(coords[0]);
  const endPoint = turf.point(coords[1]);
  const bearing = turf.bearing(startPoint, endPoint);

  // Convert to 0-360 range
  const normalizedBearing = (bearing + 360) % 360;
  return normalizedBearing;
}

// Helper function to rotate a point around another point using proper CRS handling
function rotatePoint(
  pointLng: number,
  pointLat: number,
  centerLng: number,
  centerLat: number,
  angleDegrees: number,
): { lng: number; lat: number } {
  // Use turf's transformRotate which properly handles coordinate system transformations
  const point = turf.point([pointLng, pointLat]);
  const center = [centerLng, centerLat];

  const rotated = turf.transformRotate(point, angleDegrees, {
    pivot: center,
  });

  return {
    lng: rotated.geometry.coordinates[0],
    lat: rotated.geometry.coordinates[1],
  };
}

// Helper function to get rotated plot corner coordinates
export function getRotatedPlotCorners(
  centerLng: number,
  centerLat: number,
  plotWidthDeg: number,
  plotLengthDeg: number,
  rotation: number,
): Array<[number, number]> {
  // Define corners relative to center (before rotation)
  // For AB line alignment:
  // LENGTH is parallel to AB line (driving direction)
  // WIDTH is perpendicular to AB line (swath width direction)
  const halfLength = plotLengthDeg / 2; // Half length - parallel to AB line
  const halfWidth = plotWidthDeg / 2; // Half width - perpendicular to AB line

  const corners = [
    [-halfWidth, -halfLength], // Bottom-left (min width, min length)
    [halfWidth, -halfLength], // Bottom-right (max width, min length)
    [halfWidth, halfLength], // Top-right (max width, max length)
    [-halfWidth, halfLength], // Top-left (min width, max length)
  ];

  // Rotate each corner and translate to center position
  return corners.map(([x, y]) => {
    const rotated = rotatePoint(
      centerLng + x,
      centerLat + y,
      centerLng,
      centerLat,
      rotation,
    );
    return [rotated.lng, rotated.lat];
  });
}
